/* eslint-disable max-lines */

import { ContextStore } from 'bernie-plugin-mobx';

import { getBexApiContext } from 'bernie-context';

import { adaptSearchCriteria } from 'stores/adapters/bex-api/common/search-criteria-adapter';
import { ChildModel } from 'stores/models/child-model';
import { getShoppingContextInput } from 'stores/adapters/bex-api/search/shopping-context-adapter';
import { RoomModel } from 'stores/models/room-model';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';
import { UrlParamQueryIdMapper } from 'stores/models';

import { MultiItemContext } from 'models/multi-item/multi-item-types';

import { DateUtils } from 'utils/date-utils';

import {
  BooleanValueInput,
  ChildInput,
  ClosedPeriodBounding,
  CoordinatesInput,
  DatePeriodUnit,
  DestinationInput,
  LodgingPwaPropertySearch,
  PaginationInput,
  PropertyDateRangeInput,
  RoomInput,
} from '__generated__/api';

const DEFAULT_STAY_LENGTH = '1';

export const getPropertySearchInput = (
  context: ContextStore,
  searchCriteria: SearchCriteriaModel,
  multiItemContext?: MultiItemContext | null,
  searchPagination?: PaginationInput,
  includePropertyListing?: boolean
): LodgingPwaPropertySearch.Variables => ({
  context: getBexApiContext(context),
  criteria: adaptSearchCriteria(context, searchCriteria, searchPagination),
  destination: {
    regionName: null,
    regionId: null,
    coordinates: null,
    pinnedPropertyId: null,
    propertyIds: null,
    mapBounds: null,
  },
  shoppingContext: getShoppingContextInput(multiItemContext),
  includePropertyListing,
  returnPropertyType: false,
});

/**
 * Returns the BooleanValueInput[] necessary for the secondary.booleans on the searchCriteria/criteria query variable.
 * But before that it will filter out any undefined or false values from the list.
 * @param searchCriteria - list of available search criteria URL parameters
 * @param paramQueryIds - object list of all the parameter names that we want to add to the boolean array
 * @returns BooleanValueInput[] - contains as an array element the id as the parameter name and the value as the parameter value
 */
export const getSearchCriteriaSecondaryBooleans = (
  searchCriteria: SearchCriteriaModel,
  paramQueryIds: UrlParamQueryIdMapper[]
): BooleanValueInput[] | null => {
  if (paramQueryIds?.length > 0) {
    return paramQueryIds
      .filter(id => String(searchCriteria[id?.URL_PARAM_ID as keyof SearchCriteriaModel]) === 'true')
      .map(id => ({
        id: id?.QUERY_BOOLEAN_ID,
        value: true,
      }));
  }

  return [];
};

export const getRooms = (searchCriteria: SearchCriteriaModel): RoomInput[] => {
  if (searchCriteria.rooms) {
    return searchCriteria.rooms.map(getRoomInput);
  }

  // Return room input with default data if searchCriteria is not present
  return [new RoomModel({ ...RoomModel.DEFAULTS })].map(getRoomInput);
};

export const getFlexSearchCriteria = (searchCriteria: SearchCriteriaModel) => {
  const flexibilityUrlParam = searchCriteria.flexibility;

  if (flexibilityUrlParam) {
    const flexibilityUrlParamArray = typeof flexibilityUrlParam === 'string' ? [flexibilityUrlParam] : Array.from(flexibilityUrlParam);

    const flexibility = flexibilityUrlParamArray
      .map(param => param.split('_'))
      .filter(params => params.length > 1)
      .map(params => {
        const length = params[0] ? parseInt(params[0], 10) : 0;
        const unit: DatePeriodUnit = (<any>DatePeriodUnit)[params[1]?.toUpperCase() ?? ''];
        const bound: ClosedPeriodBounding = params[2] && (<any>ClosedPeriodBounding)[params[2].toUpperCase()];

        return bound ? { length, unit, bound } : { length, unit };
      })
      .filter(({ length, unit }) => length && !isNaN(length) && unit !== undefined);

    return flexibility.length > 0 ? { flexibility } : null;
  }

  return null;
};

const generatePropertyDateRangeInput = (checkInDate?: string, checkOutDate?: string): PropertyDateRangeInput | null => {
  const startDate = DateUtils.parseDateString(checkInDate);
  const endDate = DateUtils.parseDateString(checkOutDate);

  if (!startDate || !endDate) return null;

  return {
    checkInDate: startDate,
    checkOutDate: endDate,
  };
};

export const getDateRangeFromSearchCriteria = (searchCriteria: SearchCriteriaModel): PropertyDateRangeInput | null => {
  const { startDate, endDate, partialStay, partialStayStartDate, partialStayEndDate } = searchCriteria;
  if (partialStay === 'true') {
    return generatePropertyDateRangeInput(partialStayStartDate, partialStayEndDate);
  }

  return generatePropertyDateRangeInput(startDate, endDate);
};

export const getDateRangeInput = (searchCriteria: SearchCriteriaModel): PropertyDateRangeInput | null => {
  const dateRange = getDateRangeFromSearchCriteria(searchCriteria);

  if (!dateRange) {
    return generateFutureDates(searchCriteria);
  }

  return {
    ...dateRange,
  };
};

export const getDestinationInput = (searchCriteria: SearchCriteriaModel): DestinationInput => {
  const coordinates: CoordinatesInput | null = getCoordinates(searchCriteria.latLong);

  let mapBounds: CoordinatesInput[] | null = null;

  if (searchCriteria.mapBounds && searchCriteria.mapBounds.length === 2) {
    const [xCoordinate, yCoordinate] = [getCoordinates(searchCriteria.mapBounds[0]), getCoordinates(searchCriteria.mapBounds[1])];
    if (xCoordinate && yCoordinate) {
      mapBounds = [xCoordinate, yCoordinate];
    }
  }

  return {
    regionName: searchCriteria.destination || null,
    regionId: getRegionId(searchCriteria.regionId),
    coordinates,
    pinnedPropertyId: searchCriteria.selected || null,
    propertyIds: getPropertyIds(searchCriteria),
    mapBounds,
  };
};

const getRegionId = (regionId?: string): string | null => {
  if (!regionId) {
    return null;
  }
  const parsedRegionId = Number.parseInt(regionId, 10);
  if (isNaN(parsedRegionId)) {
    return null;
  }

  return regionId;
};

export const getCoordinates = (latLong?: string): CoordinatesInput | null => {
  if (!latLong) {
    return null;
  }

  const latLongParts = latLong.toString().split(',') || [];

  if (!latLongParts || latLongParts.length !== 2 || !latLongParts[0] || !latLongParts[1]) {
    return null;
  }

  const parsedLat = Number.parseFloat(latLongParts[0]);
  const parsedLong = Number.parseFloat(latLongParts[1]);

  if (isNaN(parsedLat) || isNaN(parsedLong)) {
    return null;
  }

  return {
    latitude: Number.parseFloat(latLongParts[0]),
    longitude: Number.parseFloat(latLongParts[1]),
  };
};

const getRoomInput = (roomModel: RoomModel): RoomInput => ({
  adults: roomModel.adults,
  children: roomModel.children?.map(getChildInput),
});

const getChildInput = (childModel: ChildModel): ChildInput => ({
  age: childModel.age,
});

const getPropertyIds = (searchCriteria: SearchCriteriaModel): string[] | null => {
  if (searchCriteria.group && searchCriteria.group.length > 0) {
    return searchCriteria.group.split(',');
  }

  return null;
};

export const generateFutureDates = (searchCriteria: SearchCriteriaModel): PropertyDateRangeInput | null => {
  if (!searchCriteria?.daysInFuture) {
    return null;
  }
  const stayLength = searchCriteria.stayLength ? searchCriteria.stayLength : DEFAULT_STAY_LENGTH;
  const checkInDate = new Date();
  checkInDate.setDate(checkInDate.getDate() + parseInt(searchCriteria.daysInFuture, 10));
  const checkOutDate = new Date(checkInDate);
  checkOutDate.setDate(checkInDate.getDate() + parseInt(stayLength, 10));
  if (isInvalidDate(checkInDate) || isInvalidDate(checkOutDate)) {
    return null;
  }

  return generatePropertyDateRangeInput(checkInDate.toISOString().split('T')[0], checkOutDate.toISOString().split('T')[0]);
};

const isInvalidDate = (date: Date) => isNaN(date.getDate());
